import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Language } from '../services/adminService'
import { adminService } from '../services/adminService'
import { Button } from '../components/Button'
import { LanguageForm } from '../components/LanguageForm'
import { LoadingScreen } from '../components/LoadingScreen'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { toast } from 'react-hot-toast'

export function LanguageEditPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams({ from: '/languages/$languageCode' })
  const languageCode = params.languageCode
  const isCreateMode = languageCode === 'create'
  const [language, setLanguage] = useState<Language | null>(null)
  const [loading, setLoading] = useState(true)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    loadLanguage()
  }, [languageCode])

  const loadLanguage = async () => {
    if (isCreateMode) {
      setLoading(false)
      return
    }

    if (!languageCode) {
      navigate({ to: '/languages' })
      return
    }

    try {
      setLoading(true)
      const data = await adminService.getLanguageByCode(languageCode)
      if (!data) {
        toast.error(t('messages.languageNotFound'))
        navigate({ to: '/languages' })
        return
      }
      setLanguage(data)
    } catch (error) {
      console.error('Error loading language:', error)
      toast.error(t('messages.languageLoadFailed'))
      navigate({ to: '/languages' })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (data: Partial<Language>) => {
    try {
      if (isCreateMode) {
        await adminService.createLanguage(data as Omit<Language, 'id' | 'createdAt' | 'updatedAt'>)
        toast.success(t('messages.languageCreatedSuccess'))
      } else {
        if (!language) return
        await adminService.updateLanguage(language.id, data)
        toast.success(t('messages.languageUpdatedSuccess'))
      }
      navigate({ to: '/languages' })
    } catch (error) {
      console.error('Error saving language:', error)
      toast.error(isCreateMode ? t('messages.languageCreateFailed') : t('messages.languageUpdateFailed'))
    }
  }

  const handleDelete = async () => {
    if (!language) return

    try {
      setDeleting(true)
      await adminService.deleteLanguage(language.id)
      toast.success(t('messages.languageDeletedSuccess'))
      navigate({ to: '/languages' })
    } catch (error) {
      console.error('Error deleting language:', error)
      toast.error(t('messages.languageDeleteFailed'))
    } finally {
      setDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  if (!isCreateMode && loading) {
    return <LoadingScreen />
  }

  if (!isCreateMode && !language) {
    return null
  }

  return (
    <div className="p-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left side - Back button and title */}
          <div className="flex flex-col items-start gap-2">
            <button
              onClick={() => navigate({ to: '/languages' })}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-200 bg-gray-50 rounded-lg transition-all duration-200 group"
              aria-label={t('buttons.back')}
            >
              <svg 
                className="w-5 h-5 transition-transform group-hover:-translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">{t('buttons.back')}</span>
            </button>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {isCreateMode ? t('languages.createLanguage') : t('languages.editLanguage')}
              </h1>
              <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
                {isCreateMode 
                  ? t('languages.createSubtitle', { defaultValue: 'Add a new supported language' })
                  : t('languages.editSubtitle', { defaultValue: 'Manage language settings and translations' })
                }
              </p>
            </div>
          </div>

          {/* Right side - Action buttons */}
          {!isCreateMode && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleting}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t('buttons.delete')}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <LanguageForm
            language={language}
            onSubmit={handleSubmit}
            onCancel={() => navigate({ to: '/languages' })}
            isLoading={loading}
          />
        </div>
      </div>

      {!isCreateMode && language && (
        <ConfirmDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDelete}
          title={t('languages.deleteConfirmTitle')}
          message={t('messages.deleteLanguageConfirm', { name: language.name })}
          confirmLabel={t('buttons.delete')}
          confirmVariant="danger"
          loading={deleting}
        />
      )}
    </div>
  )
}